import React, { useEffect, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import '../pages.css';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { LoadingState, ErrorState } from '../../components/ui/States';
import { useToast } from '../../components/ui/Toast';
import { useAuth, authHeaders } from '../../auth';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_BASE = `${API_URL}/api`;

const SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'];
const SEVERITY_LABELS = { INFO: 'Information', WARNING: 'Avertissement', CRITICAL: 'Critique' };

function RuleCard({ rule, onSave }) {
  const [enabled, setEnabled] = useState(rule.enabled);
  const [threshold, setThreshold] = useState(rule.threshold);
  const [severity, setSeverity] = useState(rule.severity);
  const [cooldownMinutes, setCooldownMinutes] = useState(rule.cooldownMinutes);
  const [saving, setSaving] = useState(false);
  const isOffline = rule.metric === 'offline';
  const isForecastWindow = rule.metric === 'thunderstorm_risk';

  const dirty = enabled !== rule.enabled || threshold !== rule.threshold || severity !== rule.severity || cooldownMinutes !== rule.cooldownMinutes;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(rule.id, { enabled, threshold: Number(threshold), severity, cooldownMinutes: Number(cooldownMinutes) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="summary-card" style={{ marginBottom: 14 }}>
      <div className="section-header-row" style={{ marginBottom: 10 }}>
        <h3 className="section-title" style={{ margin: 0 }}>{rule.name}</h3>
        <label className="checkbox-item" style={{ fontSize: '0.85rem' }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Activée
        </label>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>{isOffline ? `Hors ligne après (minutes)` : isForecastWindow ? `Fenêtre de prévision (heures)` : `Condition: ${rule.operator} (${rule.unit})`}</label>
          <input
            type="number"
            step="0.5"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            style={{ maxWidth: 120 }}
          />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Sévérité</label>
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={{ maxWidth: 160 }}>
            {SEVERITIES.map((s) => <option key={s} value={s}>{SEVERITY_LABELS[s]}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>Délai de répétition (min)</label>
          <input
            type="number"
            step="1"
            value={cooldownMinutes}
            onChange={(e) => setCooldownMinutes(e.target.value)}
            style={{ maxWidth: 100 }}
          />
        </div>
        <button className="btn-primary" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      {rule.note && <div className="summary-sub" style={{ marginTop: 10 }}>{rule.note}</div>}
    </div>
  );
}

export default function AlertRulesPage() {
  const { token } = useAuth();
  const toast = useToast();
  const [rules, setRules] = useState(null);
  const [error, setError] = useState(false);

  const headers = { 'Content-Type': 'application/json', ...authHeaders(token) };

  const load = async () => {
    setError(false);
    try {
      const res = await fetch(`${API_BASE}/admin/alert-rules`, { headers });
      if (!res.ok) throw new Error('load failed');
      setRules(await res.json());
    } catch {
      setRules([]);
      setError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const saveRule = async (id, updates) => {
    try {
      const res = await fetch(`${API_BASE}/admin/alert-rules/${id}`, { method: 'PUT', headers, body: JSON.stringify(updates) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur de mise à jour');
      toast.success('Règle mise à jour');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Configuration des Alertes' }]} />
      <div className="page-header">
        <h1><SlidersHorizontal size={22} /> Configuration des Alertes</h1>
      </div>

      {rules === null ? (
        <LoadingState label="Chargement des règles..." />
      ) : error ? (
        <ErrorState label="Impossible de charger les règles d'alerte" />
      ) : (
        <>
          <div className="section">
            <h3 className="section-title">Règles Capteurs (temps réel)</h3>
            {rules.filter((r) => r.sourceType !== 'forecast').map((rule) => (
              <RuleCard key={rule.id} rule={rule} onSave={saveRule} />
            ))}
          </div>

          <div className="section" style={{ marginTop: 24, borderTop: '1px solid #333', paddingTop: 16 }}>
            <h3 className="section-title">Prévisions Météo (Open-Meteo)</h3>
            <div className="summary-sub" style={{ marginBottom: 14 }}>
              Ces règles évaluent les prévisions Open-Meteo (pas de capteur en temps réel) toutes les 30 minutes,
              uniquement pour les centrales dont les coordonnées ont été configurées par un administrateur.
              Désactivées par défaut - à activer volontairement après vérification des seuils.
            </div>
            {rules.filter((r) => r.sourceType === 'forecast').map((rule) => (
              <RuleCard key={rule.id} rule={rule} onSave={saveRule} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
